import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";

// Schema de validação baseado no JSON fornecido
const formSchema = z.object({
  readingTime: z.string().min(1, "Por favor, selecione o horário de leitura"),
  interestSection: z.string().min(1, "Por favor, selecione a seção de interesse"),
  perception: z.string().min(1, "Por favor, selecione sua percepção"),
  comment: z.string().optional(),
  email: z.string().email("E-mail inválido").min(1, "E-mail é obrigatório"),
});

type FormValues = z.infer<typeof formSchema>;

const FORM_CONFIG = {
  color: '#FECF04',
  logo: '/thenews.webp',
  title: 'Formulário do CEO',
  description: 'Pesquisa rápida sobre hábitos e percepções dos leitores do the news',
};

const FIELD_IDS = {
  readingTime: "Jo19SiBLeGiS5ESSkpCGM",
  interestSection: "mhx_pQmbQmlOJwA4cefsk",
  perception: "yihD-M7yCTyttmlpOqUzg",
  comment: "Vce8JcScZZDdL37SAaVP0",
  email: "email", // Não existe no JSON, mas usamos para userId
};

function OptionButton({ value, selected, onClick, children, className = "" }: { value: string, selected: boolean, onClick: (v: string) => void, children: React.ReactNode, className?: string }) {
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      className={`flex items-center justify-center px-3 py-2 rounded-lg border text-sm font-sm transition-colors
        ${selected ? 'bg-[var(--newsletter-color)] text-white border-[var(--newsletter-color)]' : 'bg-gray-100 text-gray-900 border-gray-200 hover:bg-[var(--newsletter-color)]/10'}
        ${className}
      `}
      style={{ minHeight: 36 }}
    >
      <span className="flex-1 text-left whitespace-pre-line">{children}</span>
    </button>
  );
}

export function CEOForm() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const emailParam = searchParams.get('email') || "";

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      readingTime: "",
      interestSection: "",
      perception: "",
      comment: "",
      email: emailParam,
    },
    mode: "onChange"
  });

  useEffect(() => {
    form.setValue('email', emailParam);
  }, [emailParam]);

  useEffect(() => {
    const values = form.getValues();
    const totalFields = 4; // Só os obrigatórios
    const filledFields = [values.readingTime, values.interestSection, values.perception].filter(v => v !== "").length + (values.comment ? 1 : 0);
    const progress = (filledFields / totalFields) * 100;
    setProgress(progress);
  }, [form.watch()]);

  const onSubmit = async (data: FormValues) => {
    try {
      setLoading(true);
      setError("");

      const formData = {
        formId: "klUU3BE2r3bw6gcbuyoVl",
        userId: emailParam,
        metadata: {
          source: "newsletter",
          campaign: "ceo_form_2025"
        },
        values: [
          { fieldId: FIELD_IDS.readingTime, value: data.readingTime },
          { fieldId: FIELD_IDS.interestSection, value: data.interestSection },
          { fieldId: FIELD_IDS.perception, value: data.perception },
          { fieldId: FIELD_IDS.comment, value: data.comment || "" },
        ]
      };

      const response = await fetch('https://backend.testeswaffle.org/forms/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Erro ao enviar formulário' }));
        throw new Error(errorData.message || 'Erro ao enviar formulário');
      }

      setIsSuccess(true);
      form.reset();
      setTimeout(() => {
        setIsSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Erro:', error);
      setError(error instanceof Error ? error.message : 'Ocorreu um erro ao enviar o formulário. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (values: FormValues) => {
    form.setValue('email', emailParam);
    return onSubmit({ ...values, email: emailParam });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className={`animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-[${FORM_CONFIG.color}]`}></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex items-center justify-center pt-8 relative pb-20" style={{ '--newsletter-color': FORM_CONFIG.color } as React.CSSProperties}>
      <div className="w-full max-w-2xl p-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex-1 flex flex-col justify-center text-center"
        >
          <img 
            src={FORM_CONFIG.logo}
            alt={FORM_CONFIG.title}
            className="w-40 mx-auto mb-6"
          />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Formulário do Editor Chefe</h1>
          <p className="text-lg text-gray-700 mb-8">{FORM_CONFIG.description}</p>

          {isSuccess ? (
            <motion.div 
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="bg-white rounded-3xl p-12 shadow-sm border border-gray-100 max-w-2xl mx-auto text-center"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", duration: 0.5 }}
                className="flex items-center justify-center w-20 h-20 rounded-full bg-[var(--newsletter-color)] text-white mx-auto mb-6"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </motion.div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Resposta enviada com sucesso! 🎉</h2>
              <p className="text-lg text-gray-700 font-['Inter']">
                Obrigado por participar da nossa pesquisa!
              </p>
            </motion.div>
          ) : (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                  className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100 max-w-2xl mx-auto"
                >
                  <div className="space-y-6">
                    <FormField
                      control={form.control}
                      name="readingTime"
                      render={({ field }) => (
                        <FormItem>
                          <h3 className="text-lg font-semibold text-gray-900 mb-4">Que horas você lê o seu the news?</h3>
                          <FormControl>
                            <div className="flex flex-col gap-3 w-full">
                              {["06:06", "Antes das 12h", "Depois das 12h"].map((option) => (
                                <OptionButton
                                  key={option}
                                  value={option}
                                  selected={field.value === option}
                                  onClick={field.onChange}
                                  className="w-full justify-start"
                                >
                                  {option}
                                </OptionButton>
                              ))}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="interestSection"
                      render={({ field }) => (
                        <FormItem>
                          <h3 className="text-lg font-semibold text-gray-900 mb-4">Qual seção te gera mais interesse?</h3>
                          <FormControl>
                            <div className="flex flex-col gap-3 w-full">
                              {["Política", "Brasil", "Negócios", "Mundo", "Economia"].map((option) => (
                                <OptionButton
                                  key={option}
                                  value={option}
                                  selected={field.value === option}
                                  onClick={field.onChange}
                                  className="w-full justify-start"
                                >
                                  {option}
                                </OptionButton>
                              ))}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="perception"
                      render={({ field }) => (
                        <FormItem>
                          <h3 className="text-lg font-semibold text-gray-900 mb-4">Para você, o the news é:</h3>
                          <FormControl>
                            <div className="flex flex-col gap-3 w-full">
                              {["Um jornal imparcial", "Um jornal de centro", "Um jornal de esquerda", "Um jornal de direita"].map((option) => (
                                <OptionButton
                                  key={option}
                                  value={option}
                                  selected={field.value === option}
                                  onClick={field.onChange}
                                  className="w-full justify-start"
                                >
                                  {option}
                                </OptionButton>
                              ))}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="comment"
                      render={({ field }) => (
                        <FormItem>
                          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quer deixar um comentário?</h3>
                          <FormControl>
                            <textarea
                              className="w-full border rounded-lg p-2 min-h-[80px]"
                              value={field.value}
                              onChange={field.onChange}
                              placeholder="Compartilhe sua opinião..."
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-[var(--newsletter-color)] hover:bg-[var(--newsletter-color)]/90 text-white px-6 py-4 text-base rounded-lg transition-colors mt-6"
                    disabled={loading}
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-4 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      'Enviar'
                    )}
                  </Button>
                </motion.div>

                <div className="mt-auto flex justify-center items-center space-x-4">
                  <div className="h-2 w-48 bg-gray-200 rounded-full overflow-hidden">
                    <motion.div 
                      className="h-full bg-[var(--newsletter-color)]"
                      initial={{ width: "0%" }}
                      animate={{ width: `${progress}%` }}
                      transition={{ duration: 0.3 }}
                    />
                  </div>
                  <span className="text-base md:text-lg text-gray-500">
                    {Math.round(progress)}% completo
                  </span>
                </div>

                {error && (
                  <motion.p
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-red-500 text-center mt-2"
                  >
                    {error}
                  </motion.p>
                )}
              </form>
            </Form>
          )}
        </motion.div>

        {/* Barra de Progresso Fixa */}
        <div className="fixed left-0 bottom-0 w-full h-2 bg-gray-200 z-50">
          <motion.div 
            className="h-full bg-[var(--newsletter-color)]"
            initial={{ width: "0%" }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
      </div>
    </div>
  );
} 